/**
 * Axios instance for Quizi.online API.
 *
 * CHANGES vs old version:
 *  1. BASE_URL changed from "/api/v1" → "/api"
 *     (Cloudflare Pages Functions live at /api/*, not /api/v1/*)
 *  2. documentsApi.upload / uploadBatch replaced with uploadText()
 *     — client now sends extracted text as JSON, NOT multipart file
 *  3. downloadPdf() updated — /export-b64 now returns structured JSON
 *     for client-side PDF rendering (jspdf), not base64 PDF bytes.
 *     The actual PDF is built client-side by pdfGenerator.js
 *  4. Everything else (auth, tests, attempts, dashboard, classrooms)
 *     is IDENTICAL — same method names, same payload shapes.
 */

import axios from "axios";

// ── In dev: CRA proxy still forwards /api → wrangler dev at :8788
// ── In production: same-origin /api → Cloudflare Pages Functions
const BASE_URL = "/api";

const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Inject JWT on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("tutor_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Global 401 handler — clear session and redirect to login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if (status === 401) {
      localStorage.removeItem("tutor_token");
      localStorage.removeItem("tutor_user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export default api;

// ── Auth ──────────────────────────────────────────────────────────────────

export const authApi = {
  login:         (email, password)                          => api.post("/auth/login",           { email, password }),
  signup:        (name, email, password, role, preferred_language) => api.post("/auth/signup",   { name, email, password, role, preferred_language }),
  me:            ()                                         => api.get("/auth/me"),
  updateMe:      (data)                                     => api.patch("/auth/me", data),
  forgotPassword:(email)                                    => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, new_password)                      => api.post("/auth/reset-password",  { token, new_password }),
};

// ── Documents ─────────────────────────────────────────────────────────────
// NEW: client extracts text first, then sends JSON to /api/documents/upload

export const documentsApi = {
  /**
   * Upload extracted text from a single document.
   * @param {object} payload
   *   { filename, extracted_text, key_terms, summary, language,
   *     subject_tag, page_count, ocr_pages }
   */
  uploadText: (payload) =>
    api.post("/documents/upload", payload),

  list:   ()     => api.get("/documents/"),
  get:    (id)   => api.get(`/documents/${id}`),
  delete: (id)   => api.delete(`/documents/${id}`),
};

// ── Questions ─────────────────────────────────────────────────────────────

export const questionsApi = {
  list:       (docId, qType) =>
    api.get(`/questions/document/${docId}`, { params: qType ? { q_type: qType } : {} }),
  stats:      (docId)        => api.get(`/questions/document/${docId}/stats`),
  regenerate: (questionId)   => api.post(`/questions/${questionId}/regenerate`),
};

// ── Tests ─────────────────────────────────────────────────────────────────

export const testsApi = {
  create:      (data) => api.post("/tests/", data),
  list:        ()     => api.get("/tests/"),
  get:         (id)   => api.get(`/tests/${id}`),
  getByCode:   (code) => api.get(`/tests/by-code/${code}`),
  delete:      (id)   => api.delete(`/tests/${id}`),
  leaderboard: (id)   => api.get(`/tests/${id}/leaderboard`),
  /**
   * Get test data needed for client-side PDF rendering.
   * Returns { format, config, sections, language, filename }
   */
  exportData:  (id, format = "question_paper") =>
    api.get(`/tests/${id}/export-b64`, { params: { format } }),
};

// ── Attempts ──────────────────────────────────────────────────────────────

export const attemptsApi = {
  submit:        (data) => api.post("/attempts/", data),
  list:          ()     => api.get("/attempts/"),
  get:           (id)   => api.get(`/attempts/${id}`),
  getTestByCode: (code) => api.get(`/attempts/test-by-code/${code}`),
};

// ── Dashboard ─────────────────────────────────────────────────────────────

export const dashboardApi = {
  student:        ()          => api.get("/dashboard/student"),
  teacher:        ()          => api.get("/dashboard/teacher"),
  studentProfile: (studentId) => api.get(`/dashboard/teacher/student/${studentId}`),
};

// ── Generate ──────────────────────────────────────────────────────────────

export const generateApi = {
  questions: (data) => api.post("/generate/questions", data),
};

// ── Classrooms ────────────────────────────────────────────────────────────

export const classroomsApi = {
  create:          (data)                  => api.post("/classrooms/", data),
  listMine:        ()                      => api.get("/classrooms/my"),
  get:             (id)                    => api.get(`/classrooms/${id}`),
  assignTest:      (classroomId, data)     => api.post(`/classrooms/${classroomId}/assign-test`, data),
  unassignTest:    (classroomId, testId)   => api.delete(`/classrooms/${classroomId}/assign-test/${testId}`),
  deleteClassroom: (id)                    => api.delete(`/classrooms/${id}`),
  join:            (join_code)             => api.post("/classrooms/join", { join_code }),
  studentClassrooms: ()                    => api.get("/classrooms/student/my"),
  leaveClassroom:  (id)                    => api.delete(`/classrooms/student/leave/${id}`),
};

// ── Results PDF Download ──────────────────────────────────────────────────

export const downloadTestResults = async (testId) => {
  const token = localStorage.getItem("tutor_token");
  const res   = await fetch(`/api/tests/${testId}/results-data`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch results");
  const data = await res.json();
  const { generateTestResultsPdf } = await import("./resultsPdfGenerator.js");
  generateTestResultsPdf({
    testTitle:       data.test_title,
    assignedCode:    data.assigned_code,
    totalMarks:      data.total_marks,
    institutionName: data.institution_name,
    attempts:        data.attempts,
  });
};

export const downloadClassroomResults = async (classroomId) => {
  const token = localStorage.getItem("tutor_token");
  const res   = await fetch(`/api/classrooms/${classroomId}/results-data`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch results");
  const data = await res.json();
  const { generateClassroomResultsPdf } = await import("./resultsPdfGenerator.js");
  generateClassroomResultsPdf({
    classroomName:   data.classroom_name,
    joinCode:        data.join_code,
    institutionName: data.institution_name,
    tests:           data.tests,
    members:         data.members,
  });
};

// ── PDF Download (client-side rendering via jspdf) ────────────────────────
/**
 * Download exam paper or answer key as PDF.
 *
 * NEW FLOW:
 *  1. Fetch structured test data from /api/tests/:id/export-b64
 *  2. Build PDF client-side using generatePdf() from pdfGenerator.js
 *  3. Trigger <a> download — same IDM-bypass pattern as before
 *
 * The pdfGenerator handles both English (NotoSans) and Urdu (NotoNastaliqUrdu).
 */
export const downloadPdf = async (testId, format = "question_paper") => {
  // Lazy-load the PDF generator to avoid increasing initial bundle size
  const { generatePdf } = await import("./pdfGenerator.js");

  const res  = await testsApi.exportData(testId, format);
  const data = res.data; // { format, config, sections, language, filename, is_answer_key }

  if (!data?.sections?.length) throw new Error("Empty test data received");

  const filename  = data.filename;
  const pdfResult = await generatePdf(data);

  // generatePdf returns ArrayBuffer — wrap in Uint8Array for base64 encoding
  const pdfBytes = pdfResult instanceof ArrayBuffer
    ? new Uint8Array(pdfResult)
    : pdfResult;

  // Encode to base64 and trigger download — same IDM-bypass anchor trick
  const base64 = _uint8ToBase64(pdfBytes);
  const a      = document.createElement("a");
  a.href       = `data:application/pdf;base64,${base64}`;
  a.download   = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

function _uint8ToBase64(bytes) {
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
